import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { compareOperationalRecords } from "../lib/sortUtils";
import { HorseIcon } from "../lib/icons";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";

export function createInspectionsModule(deps){
  const {
    useState,
    useEffect,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useSysLists,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    SYSTEM_LISTS_DEFAULTS,
    getAccounts,
    createClientId,
    makeCreateMeta,
    makeUpdateMeta,
    canCreateInModule,
    canEditInModule,
    canViewCostCenter,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    getDeniedCostCentersForModule,
    itemMatchesCostCenterAccess,
    T,
    COST_CENTERS,
    COLLAB_LEVELS,
    today,
    fmtDate,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    CCBadge,
  } = deps;

  const compactLayout = () => (typeof window !== "undefined" ? window.innerWidth <= 820 : false);

  const CompactFilterField = ({ label, children }) => (
    <div style={{display:"grid",gap:4,minWidth:0}}>
      <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.6,lineHeight:1.1}}>{label}</div>
      {children}
    </div>
  );

  const CompactSelect = ({ children, style, ...props }) => (
    <select
      {...props}
      style={{
        width:"100%",
        minHeight:38,
        height:38,
        borderRadius:12,
        border:`1px solid ${T.border}`,
        background:T.card2,
        color:T.text,
        padding:"0 36px 0 10px",
        fontSize:13,
        lineHeight:1.2,
        outline:"none",
        textOverflow:"ellipsis",
        whiteSpace:"nowrap",
        overflow:"hidden",
        ...style,
      }}
    >
      {children}
    </select>
  );

const INSPECTION_TARGETS = [
  { id:"haras",    icon:<HorseIcon size={18} />, label:"Baias do Haras",              costCenter:"Haras"     },
  { id:"pasture",  icon:"🌿", label:"Pastos e Áreas",              costCenter:"Pecuária"  },
  { id:"facility", icon:"🏗️", label:"Instalações (Galpão/Curral/Cerca)", costCenter:"Sede" },
  { id:"machinery",icon:"🚜", label:"Maquinário e Equipamentos",   costCenter:"Pecuária"  },
  { id:"stock",    icon:"📦", label:"Estoque e Almoxarifado",      costCenter:"Sede"      },
  { id:"epi",      icon:"🦺", label:"EPIs e Segurança",            costCenter:"Sede"      },
];

// Checklist base por tipo de alvo
const BASE_CHECKLIST = {
  haras:    ["Estado geral das baias","Aguada (bebedouros)","Cocho de ração","Cama dos animais","Iluminação","Ventilação","Presença de pragas"],
  pasture:  ["Estado da cerca","Disponibilidade de água","Qualidade do pasto","Acesso ao local","Saleiro/cocho","Sombra disponível"],
  facility: ["Estrutura física (paredes/telhado)","Portas e janelas","Instalação elétrica","Limpeza geral","Segurança (cadeados/portões)","Sinalização"],
  machinery:["Estado geral","Vazamentos (óleo/combustível)","Pneus/esteiras","Documentação/revisão em dia","Extintor","EPI disponível"],
  stock:    ["Organização dos itens","Validade dos produtos","Controle de pragas","Umidade e temperatura","Identificação dos produtos","Acesso e segurança"],
  epi:      ["Capacetes disponíveis","Botas em bom estado","Luvas","Protetor auricular","Cinto de segurança","Equipamentos de primeiros socorros"],
};

const FINDING_SEVERITY = {
  "Conforme":     { color:"#22C55E", icon:"✅", abbr:"C" },
  "Não Conforme": { color:"#EF4444", icon:"🔴", abbr:"NC" },
  "Atenção":      { color:"#F59E0B", icon:"⚠️", abbr:"AT" },
  "N/A":          { color:"#64748B", icon:"—",  abbr:"N/A" },
};

function InspectionsModule({ session, isGestor, openInspectionId, onBridgeBack }) {
  const [inspections, setInspections] = useFarmCollection("inspections", []);
  const lists = useSysLists();
  const costCenterCodeMap = React.useMemo(()=>mapCostCenterCodes(lists?.COST_CENTER_CODES || SYSTEM_LISTS_DEFAULTS.COST_CENTER_CODES || []), [lists]);
  const [pastures,   ]                = useFarmCollection("pastures",    []);
  const [adminGlobalsConfigRows]      = useGlobalCollection("adminGlobalsConfig", []);
  const adminGlobalsConfig            = React.useMemo(()=> (adminGlobalsConfigRows||[])[0] || null, [adminGlobalsConfigRows]);
  const [machines,   ]                = useFarmCollection("machinery",   []);
  const [, setOccurrences] = useFarmCollection("occurrences", []);
  const [,           setServices]    = useFarmCollection("services",    []);
  const [employees,  ]                = useFarmCollection("employees",   []);
  const [, setNotifications]          = useFarmCollection("notifications", []);
  const [tab,         setTab]         = useState("list");
  const [form,        setForm]        = useState(null);   // active inspection being filled
  const [detailInsp,  setDetailInsp]  = useState(null);
  const [findingModal,setFindingModal]= useState(null);   // {inspId, itemIdx} → create occ/svc
  const [toast,       show]           = useToast();
  useBgSyncToast("inspections", show);
  const syncOnSave = useReliableSyncOnSave();
  const { fid:_fid } = useFarm();
  const canManage = canEditInModule(session, "inspections") || session?.role==="admin" || isGestor;
  const canCreate = canCreateInModule(session, "inspections") || canEditInModule(session, "inspections") || session?.role==="admin" || isGestor;
  const myName    = session?.name || "";
  const bridgeCostCenter = String(session?.sedeBridge?.enabled ? (session?.sedeBridge?.costCenter || "") : "").trim();
  const bridgeScoped = !!bridgeCostCenter;
  const allowedCostCenters = React.useMemo(()=> bridgeScoped ? [bridgeCostCenter] : getAllowedCostCentersForModule(session, "inspections"), [bridgeScoped, bridgeCostCenter, session]);

  async function buildInspectionNotificationRecipients(inspRecord){
    const accounts = await getAccounts();
    const farmId = String(inspRecord?.farmId || _fid || "").trim();
    const recipientsMap = new Map();
    const pushRecipient = (acc, roleOverride="")=>{
      if(!acc) return;
      const accountId = String(acc?.id || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if(!accountId && !username) return;
      const key = accountId || username;
      if(recipientsMap.has(key)) return;
      recipientsMap.set(key, { accountId, userId:String(acc?.userId || "").trim(), username, role:String(roleOverride || acc?.role || "").trim().toLowerCase(), name:String(acc?.name || "").trim() });
    };
    const canAccessFarm = (acc)=>{
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    accounts.filter(acc=>String(acc?.role||"").trim().toLowerCase()==="admin" && acc?.active!==false).forEach(acc=>pushRecipient(acc, "admin"));
    accounts.filter(acc=>String(acc?.role||"").trim().toLowerCase()==="gestor" && acc?.active!==false && canAccessFarm(acc)).forEach(acc=>pushRecipient(acc, "gestor"));
    return Array.from(recipientsMap.values());
  }

  async function appendInspectionNotification(inspRecord, meta={}){
    try{
      if(!isAdminNotificationEnabled(adminGlobalsConfig, "inspections")) return;
      const recipients = await buildInspectionNotificationRecipients(inspRecord);
      if(!recipients.length) return;
      const eventAt = String(meta?.eventAt || inspRecord?.updatedAt || inspRecord?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
      const summary = String(meta?.summary || meta?.body || "").trim() || `Vistoria ${inspRecord?.recordNumber || inspRecord?.id || ""} atualizada.`;
      const notifId = createClientId("ntf");
      const notification = {
        id:notifId,
        clientId:notifId,
        farmId:String(inspRecord?.farmId || _fid || "").trim(),
        type:"inspection_event",
        event:String(meta?.event || "inspection_updated").trim() || "inspection_updated",
        eventLabel:String(meta?.eventLabel || "Atualização da vistoria").trim() || "Atualização da vistoria",
        title:String(meta?.title || "Atualização de vistoria").trim() || "Atualização de vistoria",
        summary,
        body:String(meta?.body || summary).trim() || summary,
        entityType:"inspection",
        entityId: inspRecord?.id || "",
        entityRecordNumber: inspRecord?.recordNumber || "",
        orderId: inspRecord?.id || "",
        orderRecordNumber: inspRecord?.recordNumber || "",
        notificationModuleId:"inspections",
        notificationSectionKey:"inspections",
        status: meta?.status || inspRecord?.overall || inspRecord?.status || "Em Andamento",
        orderStatus: meta?.status || inspRecord?.overall || inspRecord?.status || "Em Andamento",
        previousOrderStatus: meta?.previousStatus || "",
        costCenter: inspRecord?.costCenter || "",
        requester: inspRecord?.inspector || session?.name || myName || "Sistema",
        recipients,
        openedInApp:false,
        readBy:[],
        eventAt,
        ...makeCreateMeta(session?.name || myName || "", { updatedAt:eventAt }),
      };
      setNotifications(prev=>[...(Array.isArray(prev)?prev:[]), notification]);
      syncOnSave("notifications");
    }catch(_e){}
  }

  const deniedCostCenters = React.useMemo(()=> bridgeScoped ? COST_CENTERS.filter(cc=>String(cc).toLowerCase()!==bridgeCostCenter.toLowerCase()) : getDeniedCostCentersForModule(session, "inspections"), [bridgeScoped, bridgeCostCenter, session]);
  const matchesBridgeCostCenter = React.useCallback((item)=> !bridgeScoped || String(item?.costCenter || "").trim().toLowerCase()===bridgeCostCenter.toLowerCase(), [bridgeScoped, bridgeCostCenter]);

  const dedupeBy = (list, keyFn) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : []).filter((item) => {
      const key = keyFn(item);
      if(!key) return true;
      if(seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const makeInspectionChecklistItem = React.useCallback((entry = {})=>{
    const stableId = String(entry.clientId || entry.id || createClientId("ii"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: String(entry.clientId || stableId),
    };
  }, []);

  const makeInspectionFindingEntry = React.useCallback((entry = {})=>{
    const stableId = String(entry.clientId || entry.id || createClientId("ifnd"));
    return {
      ...entry,
      id: String(entry.id || stableId),
      clientId: String(entry.clientId || stableId),
    };
  }, []);

  const normalizeInspection = React.useCallback((item) => {
    if(!item || typeof item !== "object") return item;
    const now = new Date().toISOString();
    return {
      ...item,
      id: typeof item.id === "string" && item.id ? item.id : createClientId("insp"),
      farmId: item.farmId || _fid || "",
      targetId: item.targetId || "facility",
      targetLabel: item.targetLabel || "Instalações",
      locationId: item.locationId || "geral",
      locationLabel: item.locationLabel || "Geral",
      costCenter: item.costCenter || bridgeCostCenter || COST_CENTERS[0],
      inspector: item.inspector || myName,
      startedAt: item.startedAt || item.createdAt || now,
      items: Array.isArray(item.items) ? item.items.map(makeInspectionChecklistItem) : [],
      findings: Array.isArray(item.findings) ? item.findings.map(makeInspectionFindingEntry) : [],
      generalNotes: item.generalNotes || "",
      status: item.status || "Em Andamento",
      createdAt: item.createdAt || item.updatedAt || now,
      updatedAt: item.updatedAt || item.createdAt || now,
      updatedBy: item.updatedBy || myName || "",
    };
  }, [_fid, myName, bridgeCostCenter, makeInspectionChecklistItem, makeInspectionFindingEntry]);

  const normalizeInspectionsCollection = React.useCallback((items) => dedupeBy(
    (Array.isArray(items) ? items : []).map(normalizeInspection),
    (insp) => insp?.id || `${insp?.farmId || ""}::${insp?.targetId || ""}::${insp?.locationId || ""}::${insp?.startedAt || insp?.createdAt || ""}`
  ), [normalizeInspection]);

  const ensureInspectionRecordNumbers = React.useCallback((records)=>{
    const baseRecords = Array.isArray(records) ? records.slice() : [];
    const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
      .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
    let changed = false;
    const working = baseRecords.slice();
    sorted.forEach(({ record, index })=>{
      if (!record || record.recordNumber) return;
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey:"inspections", costCenter:record?.costCenter, costCenterCodeMap });
      working[index] = { ...record, ...next };
      changed = true;
    });
    return changed ? working : baseRecords;
  }, [costCenterCodeMap]);

  const makeProvisionalInspectionRecordNumber = React.useCallback((records, payload = {})=>{
    const next = buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], {
      moduleKey:"inspections",
      costCenter: payload?.costCenter,
      costCenterCodeMap,
    });
    return { ...next, recordNumber: appendProvisionalSuffix(next.recordNumber) };
  }, [costCenterCodeMap]);

  const displayInspectionRecordNumber = React.useCallback((record)=>{
    if(!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if(!rn) return "—";
    if(record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }, []);

  const persistInspections = (updater) => {
    setInspections((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return ensureInspectionRecordNumbers(normalizeInspectionsCollection(next));
    });
  };

  useEffect(() => {
    const normalized = normalizeInspectionsCollection(inspections);
    const changed = JSON.stringify(normalized) !== JSON.stringify(inspections || []);
    if(changed) setInspections(normalized);
  }, [inspections, normalizeInspectionsCollection, setInspections]);

  // ── Access control by costCenter + collabLevel ────────────────────
  // Get this collaborator's employee record to find costCenter + level
  const myEmployee = employees.find(e=>
    String(e?.linkedAccountId||e?.accountId||"").trim()===String(session?.id||"").trim() ||
    String(e?.userId||"").trim()===String(session?.userId||"").trim() ||
    e.username?.toLowerCase()===(session?.username||"").toLowerCase() ||
    e.name?.toLowerCase()===myName.toLowerCase()
  );
  const myCostCenter  = myEmployee?.costCenter || null;
  const myLevel       = myEmployee?.collabLevel || 1;
  const myLevelConfig = COLLAB_LEVELS.find(l=>l.id===myLevel);

  // Filter inspections by permission:
  // admin/gestor: see all
  // collab level 1+: see finalized inspections of their costCenter
  // collab level 2+: also see full history
  // collab level 3: can also add comments on findings
  // Nível 1 cannot see inspections at all
  const canSeeInspections = canViewCostCenter(session, "inspections", myCostCenter || allowedCostCenters[0] || "") || canManage || (myLevel >= 2);
  const visibleInspections = (canManage ? inspections : myLevel >= 2 ? inspections.filter(i => i.status==="Finalizada" && i.costCenter===myCostCenter) : [])
    .filter(matchesBridgeCostCenter)
    .filter(i=>itemMatchesCostCenterAccess(session, "inspections", i));
  const availableTargets = INSPECTION_TARGETS.filter(t=>allowedCostCenters.includes(t.costCenter));
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.inspections;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedInspectionCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    if(Array.isArray(direct) && direct.length) return direct;
    const legacyMenu = getConfiguredSectionCards("menu:vistorias", []);
    if(Array.isArray(legacyMenu) && legacyMenu.length) return legacyMenu;
    return ["total","conforme","com_atencao","nao_conformes"];
  }, [getConfiguredSectionCards]);
  const filterConfiguredCards = React.useCallback((items=[], allowedIds=[], fallbackIds=[])=>{
    const normalizedAllowed = Array.isArray(allowedIds) ? allowedIds.map(v=>String(v||"")) : [];
    const normalizedFallback = Array.isArray(fallbackIds) ? fallbackIds.map(v=>String(v||"")) : [];
    const normalizedItems = (Array.isArray(items) ? items : []).map((item, index)=>({
      ...item,
      id: String(item?.id || normalizedFallback[index] || `card_${index}`),
    }));
    if(!normalizedAllowed.length) return [];
    return normalizedItems.filter(item=>normalizedAllowed.includes(item.id));
  }, []);
  const [statusFilter, setStatusFilter] = useState("all");
  const [cardFilter, setCardFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [customDateStart, setCustomDateStart] = useState("");
  const [customDateEnd, setCustomDateEnd] = useState("");
  const [costCenterFilter, setCostCenterFilter] = useState("all");
  const availableInspectionCostCenters = React.useMemo(()=>{
    const source = canManage ? inspections : visibleInspections;
    return Array.from(new Set((Array.isArray(source) ? source : []).map(item=>String(item?.costCenter || "").trim()).filter(Boolean)));
  }, [canManage, inspections, visibleInspections]);
  const filteredInspections = React.useMemo(()=>{
    const now = new Date();
    const list = [...visibleInspections].filter((insp)=>{
      const overall = String(insp?.overall || "");
      if(cardFilter === "total"){}
      else if(cardFilter === "conforme" && overall !== "Conforme") return false;
      else if(cardFilter === "com_atencao" && overall !== "Atenção") return false;
      else if(cardFilter === "nao_conformes" && overall !== "Não Conforme") return false;
      if(statusFilter !== "all" && overall !== statusFilter) return false;
      if(costCenterFilter !== "all" && String(insp?.costCenter || "") !== costCenterFilter) return false;
      const rawDate = insp?.finishedAt || insp?.startedAt || insp?.createdAt || "";
      const date = rawDate ? new Date(rawDate) : null;
      if(date && !Number.isNaN(date.getTime())){
        if(dateRangeFilter === "7" || dateRangeFilter === "15" || dateRangeFilter === "30"){
          const days = Number(dateRangeFilter);
          const threshold = new Date(now);
          threshold.setHours(0,0,0,0);
          threshold.setDate(threshold.getDate() - (days - 1));
          if(date < threshold) return false;
        } else if(dateRangeFilter === "custom"){
          if(customDateStart){
            const start = new Date(`${customDateStart}T00:00:00`);
            if(date < start) return false;
          }
          if(customDateEnd){
            const end = new Date(`${customDateEnd}T23:59:59`);
            if(date > end) return false;
          }
        }
      } else if(dateRangeFilter !== "all") {
        return false;
      }
      return true;
    });
    return list.sort(compareOperationalRecords);
  }, [visibleInspections, cardFilter, statusFilter, costCenterFilter, dateRangeFilter, customDateStart, customDateEnd]);

  useEffect(()=>{
    const handleBackToStart = ()=>{
      setDetailInsp(null);
      setFindingModal(null);
      setForm(null);
      setTab("list");
      setCardFilter("all");
      if(typeof onBridgeBack === "function") onBridgeBack();
    };
    window.addEventListener("fz:app:navigate-back-to-module-start", handleBackToStart);
    return ()=>window.removeEventListener("fz:app:navigate-back-to-module-start", handleBackToStart);
  }, [onBridgeBack]);



  useEffect(()=>{
    if(!openInspectionId) return;
    const match = visibleInspections.find(i=>String(i.id)===String(openInspectionId)) || inspections.find(i=>String(i.id)===String(openInspectionId));
    if(match && (!detailInsp || String(detailInsp.id)!==String(match.id))) setDetailInsp(match);
  }, [openInspectionId, visibleInspections, inspections, detailInsp]);

  // ── Location list per target type ─────────────────────────────────
  function getLocations(targetId) {
    if(targetId==="pasture")   return pastures.filter(matchesBridgeCostCenter).filter(p=>itemMatchesCostCenterAccess(session, "pastures", p)).map(p=>({id:p.id, label:p.name, costCenter:p.costCenter||"Pecuária"}));
    if(targetId==="machinery") return machines.filter(matchesBridgeCostCenter).filter(m=>itemMatchesCostCenterAccess(session, "machinery", m)).map(m=>({id:m.id, label:`${m.name} (${m.plate||m.type||""})`, costCenter:m.costCenter||"Pecuária"}));
    return [{id:"geral", label:"Geral", costCenter:(INSPECTION_TARGETS.find(t=>t.id===targetId)?.costCenter || allowedCostCenters[0] || COST_CENTERS[0])}];
  }

  // ── Start new inspection ─────────────────────────────────────────
  function startInspection(targetId, locationId, locationLabel, costCenter) {
    const target = INSPECTION_TARGETS.find(t=>t.id===targetId);
    if(bridgeScoped && costCenter !== bridgeCostCenter) return;
    if(!canEditCostCenter(session, "inspections", costCenter || target?.costCenter || allowedCostCenters[0] || COST_CENTERS[0])){ show("Sem permissão para iniciar vistoria neste centro de custo.", "error"); return; }
    const baseItems = (BASE_CHECKLIST[targetId]||[]).map((desc,i)=>makeInspectionChecklistItem({
      id:`item_${i+1}`, desc, status:"Conforme", notes:"", photo:"", finding:false,
    }));
    const startedAt = new Date().toISOString();
    const inspectionClientId = createClientId("insp");
    const draftInspection = {
      id:inspectionClientId,
      clientId:inspectionClientId,
      farmId:_fid || target?.farmId || "",
      targetId, targetLabel: target?.label||targetId,
      locationId, locationLabel,
      costCenter: costCenter || target?.costCenter || COST_CENTERS[0],
      inspector: myName,
      startedAt,
      items: baseItems,
      generalNotes: "",
      status: "Em Andamento",
      findings: [],
      ...makeProvisionalInspectionRecordNumber(inspections||[], { costCenter: costCenter || target?.costCenter || COST_CENTERS[0] }),
    };
    appendInspectionNotification(draftInspection, { event:"inspection_opened", eventLabel:"Vistoria aberta", title:"Nova vistoria aberta", summary:`${myName || "Sistema"} abriu a vistoria ${draftInspection.recordNumber || draftInspection.id}${draftInspection.costCenter ? ` · ${draftInspection.costCenter}` : ""}.`, status:"Em Andamento", eventAt: startedAt });
    setForm({
      _new: true,
      targetId, targetLabel: target?.label||targetId,
      locationId, locationLabel,
      costCenter: costCenter || target?.costCenter || COST_CENTERS[0],
      inspector: myName,
      startedAt,
      id: draftInspection.id,
      clientId: draftInspection.clientId,
      recordNumber: draftInspection.recordNumber,
      items: baseItems,
      generalNotes: "",
      status: "Em Andamento",
      findings: [],  // [{itemId, type:"occurrence"|"service", refId}]
    });
    setTab("form");
  }

  // ── Add extra item to checklist ──────────────────────────────────
  function addItem() {
    setForm(f=>({...f, items:[...f.items, makeInspectionChecklistItem({desc:"", status:"Conforme", notes:"", photo:"", finding:false})]}));
  }

  // ── Finalize inspection ──────────────────────────────────────────
  function finalizeInspection() {
    if(!form.generalNotes.trim() && form.items.some(i=>i.status==="Não Conforme")) {
      show("Adicione observações gerais antes de finalizar.", "error"); return;
    }
    const{_new,...d}=form;
    const nonConformes = d.items.filter(i=>i.status==="Não Conforme").length;
    const atencoes     = d.items.filter(i=>i.status==="Atenção").length;
    const overall = nonConformes>0?"Não Conforme":atencoes>0?"Atenção":"Conforme";

    // Assinatura automática pelo login do usuário que finalizou
    const signature = {
      name:    session?.name     || myName,
      username:session?.username || session?.name || myName,
      role:    session?.role     || "gestor",
      signedAt:new Date().toISOString(),
    };

    const finishedAt = new Date().toISOString();
    const finalizedClientId = String(d.clientId || d.id || createClientId("insp"));
    const finalized = markOperationalCreatePending({
      ...d,
      id: finalizedClientId,
      clientId: finalizedClientId,
      farmId:_fid || d.farmId || "",
      status:"Finalizada",
      finishedAt,
      updatedAt: finishedAt,
      overall, nonConformes, atencoes,
      items: (Array.isArray(d.items) ? d.items : []).map(makeInspectionChecklistItem),
      findings: (Array.isArray(d.findings) ? d.findings : []).map(makeInspectionFindingEntry),
      signature,
      ...makeCreateMeta(myName || "", { updatedAt: finishedAt }),
    }, { userName: myName || "", at: finishedAt });
    persistInspections(p=>[...p, finalized]);
    syncOnSave("inspections");
    appendInspectionNotification(finalized, { event:"inspection_result", eventLabel:"Resultado da vistoria", title:`Resultado da vistoria ${finalized.recordNumber || ""}`.trim(), summary:`${signature.name || myName || "Sistema"} finalizou a vistoria ${finalized.recordNumber || finalized.id} com resultado ${overall}.`, status: overall, previousStatus:"Em Andamento", eventAt: finalized.finishedAt || new Date().toISOString() });
    show("✅ Vistoria finalizada e registrada!");
    setForm(null); setTab("list");
  }

  // ── Create finding (occurrence or service) from checklist item ───
  function createFinding(insp, item, type, extraData) {
    let refId;
    if(type==="occurrence") {
      const catMap = {haras:"structure",pasture:"other",facility:"structure",machinery:"machines",stock:"other",epi:"other"};
      const occClientId = createClientId("occ");
      const occNumber = { recordNumber: appendProvisionalSuffix(buildNextRecordNumber([], { moduleKey:"occurrences", costCenter: insp.costCenter, costCenterCodeMap }).recordNumber || "") };
      const occTimelineId = createClientId("otl");
      const newOcc = markOperationalCreatePending({
        id: occClientId,
        clientId: occClientId,
        ...occNumber,
        title: `[Vistoria] ${item.desc} — ${insp.locationLabel}`,
        category: catMap[insp.targetId]||"other",
        description: item.notes||`Achado na vistoria de ${insp.locationLabel} em ${new Date(insp.startedAt).toLocaleDateString("pt-BR")}`,
        location: insp.locationLabel,
        urgency: item.status==="Não Conforme"?"Alta":"Normal",
        status: "Aberta",
        reportedBy: myName, reportedAt: new Date().toISOString(),
        costCenter: insp.costCenter,
        inspectionRef: insp.id || "pending",
        budget:{value:0,date:"",notes:"",photos:[]},
        repairs:[], history:[],
        timeline:[{id:occTimelineId,clientId:occTimelineId,ts:new Date().toISOString(),actor:myName,action:"🔴 Aberta via Vistoria",note:`Item: ${item.desc}`}],
        ...makeCreateMeta(myName || ""),
        ...extraData,
      }, { userName: myName || "" });
      setOccurrences(p=>[...p,newOcc]);
      syncOnSave("occurrences");
      refId = newOcc.id;
    } else {
      const svcClientId = createClientId("svc");
      const svcNumber = { recordNumber: appendProvisionalSuffix(buildNextRecordNumber([], { moduleKey:"services", costCenter: insp.costCenter, costCenterCodeMap }).recordNumber || "") };
      const svcTimelineId = createClientId("stl");
      const newSvc = markOperationalCreatePending({
        id: svcClientId,
        clientId: svcClientId,
        ...svcNumber,
        title: `[Vistoria] ${item.desc} — ${insp.locationLabel}`,
        serviceType: "Manutenção",
        description: item.notes||`Achado na vistoria de ${insp.locationLabel}`,
        costCenter: insp.costCenter,
        status: "Rascunho",
        assignees: [],
        dateStart: today(),
        timeline:[{id:svcTimelineId,clientId:svcTimelineId,ts:new Date().toISOString(),actor:myName,action:"📝 Criado via Vistoria",note:`Item: ${item.desc}`}],
        materials:[], notes:"",
        ...makeCreateMeta(myName || ""),
        ...extraData,
      }, { userName: myName || "" });
      setServices(p=>[...p,newSvc]);
      syncOnSave("services");
      refId = newSvc.id;
    }
    // Mark item as finding in form
    if(form) {
      setForm(f=>({
        ...f,
        items: f.items.map(i=>i.id===item.id?makeInspectionChecklistItem({...i,finding:true,findingType:type,findingRef:refId}):i),
        findings:[...(f.findings||[]),makeInspectionFindingEntry({itemId:item.id,itemDesc:item.desc,type,refId})],
      }));
    }
    setFindingModal(null);
    show(`✅ ${type==="occurrence"?"Ocorrência":"Serviço"} criado automaticamente!`);
  }

  // ── Stats ─────────────────────────────────────────────────────────
  const ncCount  = visibleInspections.filter(i=>i.overall==="Não Conforme").length;
  const atCount  = visibleInspections.filter(i=>i.overall==="Atenção").length;

  return(
    <div><Toast toast={toast}/>
      <ModuleHeaderMenu T={T} title="Menu de Vistorias" items={[]} />
      {!!deniedCostCenters.length && <div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.text}}>⚠️ Sem acesso a: <strong>{deniedCostCenters.join(", ")}</strong>. Registros desses centros ficam ocultos ou com ND nos relatórios.</div>}


      {/* Stats */}
      {tab!=="form"&&(()=>{
        const cards = filterConfiguredCards([
          { id:"total", node:<StatCard icon="📋" label="Total" value={visibleInspections.length} color={T.accent} onClick={()=>setCardFilter("total")}/> },
          { id:"conforme", node:<StatCard icon="✅" label="Conformes" value={visibleInspections.filter(i=>i.overall==="Conforme").length} color={T.green} onClick={()=>{ setCardFilter("conforme"); setStatusFilter("all"); }}/> },
          { id:"com_atencao", node:<StatCard icon="⚠️" label="Com Atenção" value={atCount} color={T.yellow} onClick={()=>{ setCardFilter("com_atencao"); setStatusFilter("all"); }}/> },
          { id:"nao_conformes", node:<StatCard icon="🔴" label="Não Conformes" value={ncCount} color={T.red} onClick={()=>{ setCardFilter("nao_conformes"); setStatusFilter("all"); }}/> },
        ], allowedInspectionCards, ["total","conforme","com_atencao","nao_conformes"]);
        return cards.length ? (
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10,marginBottom:20}}>
            {cards.map(item=><React.Fragment key={item.id}>{item.node}</React.Fragment>)}
          </div>
        ) : null;
      })()}

      {/* Nível 1 — blocked */}
      {!canManage&&!canSeeInspections&&(
        <div style={{background:`${T.muted}12`,border:`1px solid ${T.muted}33`,borderRadius:14,padding:"32px 24px",textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:12}}>🔒</div>
          <div style={{fontWeight:700,color:T.text,fontSize:16,marginBottom:6}}>Acesso Restrito</div>
          <div style={{fontSize:13,color:T.muted,marginBottom:4}}>
            Seu nível de acesso (<strong>{myLevelConfig?.label||"Nível 1"}</strong>) não permite visualizar vistorias.
          </div>
          <div style={{fontSize:12,color:T.muted}}>
            Contacte o administrador para solicitar elevação de nível.
          </div>
        </div>
      )}

      {/* Access banner for Nível 2 e 3 */}
      {!canManage&&canSeeInspections&&tab!=="form"&&(
        <div style={{
          background:`${(myLevelConfig?.color||T.accent)}15`,
          border:`1px solid ${(myLevelConfig?.color||T.accent)}44`,
          borderRadius:10, padding:"10px 16px", marginBottom:16,
          display:"flex", alignItems:"center", gap:12,
        }}>
          <span style={{fontSize:24}}>{myLevelConfig?.icon||"👤"}</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,color:T.text,fontSize:13}}>
              {myLevelConfig?.label} — Vistorias de {myCostCenter||"seu setor"}
            </div>
            <div style={{fontSize:11,color:T.muted,marginTop:2}}>{myLevelConfig?.desc}</div>
          </div>
          <Badge color={myLevelConfig?.color||T.accent}>{visibleInspections.length} vistoria(s)</Badge>
        </div>
      )}

      {tab!=="form"&&!detailInsp&&canSeeInspections&&(
        <div style={{display:"grid",gap:12,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div>
              <div style={{fontWeight:800,color:T.text,fontSize:16}}>📋 Lista de Vistorias</div>
              <div style={{fontSize:12,color:T.muted}}>A própria página principal já mostra o histórico. Use os filtros abaixo para localizar rapidamente as vistorias.</div>
            </div>
            {canCreate&&(
              <Btn onClick={()=>setTab("new")}>+ Iniciar Vistoria</Btn>
            )}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:compactLayout()?"10px 10px":"12px 14px",display:"grid",gap:8,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:compactLayout()?"minmax(0,1fr) minmax(0,1fr)":"repeat(3,minmax(0,1fr))",gap:8,alignItems:"end"}}>
              <CompactFilterField label="Status">
                <CompactSelect value={statusFilter} onChange={e=>{ setCardFilter("all"); setStatusFilter(e.target.value); }}>
                  <option value="all">Todos</option>
                  <option value="Conforme">Conforme</option>
                  <option value="Atenção">Com Atenção</option>
                  <option value="Não Conforme">Não Conformes</option>
                </CompactSelect>
              </CompactFilterField>
              <CompactFilterField label="Período">
                <CompactSelect value={dateRangeFilter} onChange={e=>setDateRangeFilter(e.target.value)}>
                  <option value="all">Todo período</option>
                  <option value="7">7 dias</option>
                  <option value="15">15 dias</option>
                  <option value="30">30 dias</option>
                  <option value="custom">Personalizado</option>
                </CompactSelect>
              </CompactFilterField>
              <CompactFilterField label="Centro de custo">
                <CompactSelect value={costCenterFilter} onChange={e=>setCostCenterFilter(e.target.value)}>
                  <option value="all">Todos</option>
                  {availableInspectionCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}
                </CompactSelect>
              </CompactFilterField>
            </div>
            {dateRangeFilter === "custom" && (
              <div style={{display:"grid",gridTemplateColumns:compactLayout()?"minmax(0,1fr)":"repeat(2,minmax(0,1fr))",gap:8}}>
                <CompactFilterField label="De">
                  <Input type="date" value={customDateStart} onChange={e=>setCustomDateStart(e.target.value)} style={{width:"100%",minWidth:0,minHeight:38,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}} />
                </CompactFilterField>
                <CompactFilterField label="Até">
                  <Input type="date" value={customDateEnd} onChange={e=>setCustomDateEnd(e.target.value)} style={{width:"100%",minWidth:0,minHeight:38,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}} />
                </CompactFilterField>
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:compactLayout()?"stretch":"center",gap:8,flexWrap:"wrap"}}>
              <div style={{fontSize:12,color:T.muted}}>{filteredInspections.length} vistoria(s) encontrada(s).</div>
              {(statusFilter !== "all" || dateRangeFilter !== "all" || costCenterFilter !== "all" || customDateStart || customDateEnd) && (
                <button
                  onClick={()=>{ setCardFilter("all"); setStatusFilter("all"); setDateRangeFilter("all"); setCustomDateStart(""); setCustomDateEnd(""); setCostCenterFilter("all"); }}
                  style={{background:"transparent",border:`1px solid ${T.border}`,color:T.text,borderRadius:999,padding:"7px 12px",fontSize:11,fontWeight:700,cursor:"pointer",alignSelf:compactLayout()?"flex-start":"auto",maxWidth:"100%"}}
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── NEW INSPECTION — choose target ──────────────────────────── */}
      {tab==="new"&&canCreate&&(
        <div>
          <div style={{fontWeight:700,color:T.text,fontSize:16,marginBottom:16}}>Selecione o que será vistoriado:</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12,marginBottom:24}}>
            {availableTargets.map(t=>(
              <InspectionTargetCard key={t.id} target={t}
                locations={getLocations(t.id)}
                costCenters={allowedCostCenters}
                onStart={(locId,locLabel,cc)=>startInspection(t.id,locId,locLabel,cc)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── ACTIVE FORM ─────────────────────────────────────────────── */}
      {tab==="form"&&form&&(
        <div>
          {/* Form header */}
          <div style={{background:`${T.accent}12`,border:`1px solid ${T.accent}33`,borderRadius:14,padding:"14px 18px",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontWeight:800,color:T.text,fontSize:17}}>
                  {INSPECTION_TARGETS.find(t=>t.id===form.targetId)?.icon} {form.targetLabel}
                </div>
                <div style={{fontSize:13,color:T.muted,marginTop:2}}>
                  📍 {form.locationLabel} · 🏠 {form.costCenter} · 👤 {form.inspector}
                </div>
                <div style={{fontSize:11,color:T.muted,marginTop:1}}>
                  Iniciada: {new Date(form.startedAt).toLocaleString("pt-BR")}
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn variant="ghost" onClick={()=>{setForm(null);setTab("list");}}>Cancelar</Btn>
                <Btn onClick={finalizeInspection} style={{background:`linear-gradient(90deg,${T.green},#16A34A)`,color:"#fff",border:"none"}}>
                  ✅ Finalizar Vistoria
                </Btn>
              </div>
            </div>
            {/* Summary badges */}
            <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
              {Object.entries(FINDING_SEVERITY).map(([s,cfg])=>{
                const cnt=form.items.filter(i=>i.status===s).length;
                return cnt>0?(
                  <span key={s} style={{background:`${cfg.color}22`,color:cfg.color,borderRadius:8,padding:"3px 10px",fontSize:12,fontWeight:700}}>
                    {cfg.icon} {s}: {cnt}
                  </span>
                ):null;
              })}
              {(form.findings||[]).length>0&&(
                <span style={{background:`${T.red}22`,color:T.red,borderRadius:8,padding:"3px 10px",fontSize:12,fontWeight:700}}>
                  🔗 {form.findings.length} achado(s) registrado(s)
                </span>
              )}
            </div>
          </div>

          {/* Checklist items */}
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
            {form.items.map((item,idx)=>(
              <div key={item.id} style={{
                background:T.card,
                border:`2px solid ${FINDING_SEVERITY[item.status]?.color||T.border}22`,
                borderLeft:`4px solid ${FINDING_SEVERITY[item.status]?.color||T.border}`,
                borderRadius:12,padding:"14px 16px",
              }}>
                <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:140}}>
                    {item.desc?(
                      <div style={{fontWeight:600,color:T.text,fontSize:14,marginBottom:6}}>{item.desc}</div>
                    ):(
                      <Input value={item.desc} placeholder="Descreva o item a verificar..."
                        onChange={e=>setForm(f=>({...f,items:f.items.map((x,i)=>i===idx?{...x,desc:e.target.value}:x)}))}
                        style={{marginBottom:6}}/>
                    )}
                    {/* Status buttons */}
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                      {Object.entries(FINDING_SEVERITY).map(([s,cfg])=>(
                        <div key={s}
                          onClick={()=>setForm(f=>({...f,items:f.items.map((x,i)=>i===idx?{...x,status:s}:x)}))}
                          style={{padding:"4px 12px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:item.status===s?700:400,
                            border:`1px solid ${item.status===s?cfg.color:T.border}`,
                            background:item.status===s?`${cfg.color}22`:"transparent",
                            color:item.status===s?cfg.color:T.muted,transition:"all .12s"}}>
                          {cfg.icon} {s}
                        </div>
                      ))}
                    </div>
                    {/* Notes input */}
                    <Input value={item.notes||""} placeholder="Observação sobre este item (opcional)..."
                      onChange={e=>setForm(f=>({...f,items:f.items.map((x,i)=>i===idx?{...x,notes:e.target.value}:x)}))}
                      style={{fontSize:12}}/>
                  </div>
                  {/* Photo + finding actions */}
                  <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                    {/* Photo upload */}
                    <label style={{cursor:"pointer",background:T.card2,border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 10px",fontSize:11,color:T.muted}}>
                      📷 Foto
                      <input type="file" accept="image/*" style={{display:"none"}}
                        onChange={e=>{
                          const f2=e.target.files[0];if(!f2)return;
                          const r=new FileReader();
                          r.onload=ev=>setForm(f=>({...f,items:f.items.map((x,i)=>i===idx?{...x,photo:ev.target.result}:x)}));
                          r.readAsDataURL(f2);
                        }}/>
                    </label>
                    {item.photo&&(
                      <img src={item.photo} alt="" style={{width:52,height:52,objectFit:"cover",borderRadius:8,border:`1px solid ${T.border}`,cursor:"zoom-in"}}
                        onClick={()=>window.open(item.photo,"_blank")}/>
                    )}
                    {/* Create finding — only for NC/Atenção */}
                    {(item.status==="Não Conforme"||item.status==="Atenção")&&!item.finding&&(
                      <Btn size="sm" onClick={()=>setFindingModal({inspFormItem:item,insp:form})}
                        style={{background:`${T.red}22`,color:T.red,border:`1px solid ${T.red}44`,fontSize:11}}>
                        ⚡ Registrar Achado
                      </Btn>
                    )}
                    {item.finding&&(
                      <div style={{background:`${T.green}18`,border:`1px solid ${T.green}44`,borderRadius:6,padding:"3px 8px",fontSize:10,color:T.green,fontWeight:700}}>
                        🔗 {item.findingType==="occurrence"?"Ocorrência":"Serviço"} criado
                      </div>
                    )}
                    {/* Remove custom item */}
                    {!BASE_CHECKLIST[form.targetId]?.includes(item.desc)&&(
                      <button onClick={()=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}))}
                        style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:12}}>✕</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add item + general notes */}
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <Btn variant="ghost" onClick={addItem}>+ Item extra</Btn>
          </div>

          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",marginBottom:20}}>
            <div style={{fontWeight:700,color:T.text,fontSize:13,marginBottom:8}}>📝 Observações Gerais da Vistoria *</div>
            <Textarea rows={3} value={form.generalNotes}
              placeholder="Resumo geral da vistoria, condições encontradas, recomendações..."
              onChange={e=>setForm(f=>({...f,generalNotes:e.target.value}))}/>
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>{setForm(null);setTab("list");}}>Cancelar</Btn>
            <Btn onClick={finalizeInspection} style={{background:`linear-gradient(90deg,${T.green},#16A34A)`,color:"#fff",border:"none",padding:"10px 24px"}}>
              ✅ Finalizar e Registrar Vistoria
            </Btn>
          </div>
        </div>
      )}

      {/* ── LIST / HISTORY ───────────────────────────────────────────── */}
      {tab==="list"&&!detailInsp&&(
        <div>
          {filteredInspections.length===0&&(
            <div style={{color:T.muted,textAlign:"center",padding:48}}>
              {visibleInspections.length===0 ? "Nenhuma vistoria realizada ainda." : "Nenhuma vistoria encontrada com os filtros atuais."}
              {canCreate&&visibleInspections.length===0&&<div style={{marginTop:12}}><Btn onClick={()=>setTab("new")}>+ Iniciar primeira vistoria</Btn></div>}
            </div>
          )}
          <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            {!compactLayout() && filteredInspections.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",padding:"10px 16px",background:T.card2,borderBottom:`1px solid ${T.border}`}}>
                {["Local / Tipo","Centro de Custo","Inspetor","Data","Resultado"].map(h=>(
                  <div key={h} style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8}}>{h}</div>
                ))}
              </div>
            )}
            {filteredInspections.map((insp,idx)=>{
              const cfg=FINDING_SEVERITY[insp.overall]||{color:T.muted,icon:"—"};
              const target=INSPECTION_TARGETS.find(t=>t.id===insp.targetId);
              const prevCount=inspections.filter(i=>i.id!==insp.id&&i.locationId===insp.locationId&&i.targetId===insp.targetId).length;
              if (compactLayout()) {
                return(
                  <div key={insp.id}
                    onClick={()=>setDetailInsp(insp)}
                    style={{padding:"14px 14px 12px",cursor:"pointer",
                      borderBottom:idx<filteredInspections.length-1?`1px solid ${T.border}`:"none",
                      background:T.card,transition:"background .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.card2}
                    onMouseLeave={e=>e.currentTarget.style.background=T.card}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
                      <div style={{display:"flex",alignItems:"flex-start",gap:10,minWidth:0,flex:1}}>
                        <span style={{fontSize:20,flexShrink:0}}>{target?.icon||"📋"}</span>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayInspectionRecordNumber(insp)}</div>
                          <div style={{fontWeight:700,color:T.text,fontSize:14,lineHeight:1.2}}>{insp.targetLabel}</div>
                          <div style={{fontSize:12,color:T.muted,marginTop:4}}>📍 {insp.locationLabel}</div>
                        </div>
                      </div>
                      <Badge color={cfg.color}>{cfg.icon} {insp.overall||"—"}</Badge>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 12px"}}>
                      <div>
                        <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.7}}>Centro de custo</div>
                        <div style={{marginTop:4}}><CCBadge cc={insp.costCenter}/></div>
                      </div>
                      <div>
                        <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.7}}>Data</div>
                        <div style={{fontSize:12,color:T.text,marginTop:4}}>{fmtDate(insp.finishedAt||insp.startedAt)}</div>
                      </div>
                      <div>
                        <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.7}}>Inspetor</div>
                        <div style={{fontSize:12,color:T.text,marginTop:4,wordBreak:"break-word"}}>{insp.inspector}</div>
                      </div>
                      <div>
                        <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.7}}>Indicadores</div>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:4}}>
                          {(insp.findings||[]).length>0&&<span style={{fontSize:11,color:T.red}}>🔗 {insp.findings.length}</span>}
                          {prevCount>0&&<span style={{fontSize:11,color:T.muted}}>📅 {prevCount} ant.</span>}
                          {(insp.findings||[]).length===0 && prevCount===0 && <span style={{fontSize:11,color:T.muted}}>—</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return(
                <div key={insp.id}
                  onClick={()=>setDetailInsp(insp)}
                  style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",padding:"12px 16px",cursor:"pointer",
                    borderBottom:idx<filteredInspections.length-1?`1px solid ${T.border}`:"none",
                    background:T.card,transition:"background .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.card2}
                  onMouseLeave={e=>e.currentTarget.style.background=T.card}>
                  <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                    <span style={{fontSize:20,flexShrink:0}}>{target?.icon||"📋"}</span>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayInspectionRecordNumber(insp)}</div>
                      <div style={{fontWeight:600,color:T.text,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{insp.targetLabel}</div>
                      <div style={{fontSize:11,color:T.muted}}>📍 {insp.locationLabel}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center"}}>
                    <CCBadge cc={insp.costCenter}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center"}}>
                    <span style={{fontSize:12,color:T.muted}}>{insp.inspector}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center"}}>
                    <span style={{fontSize:12,color:T.muted}}>{fmtDate(insp.finishedAt||insp.startedAt)}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <Badge color={cfg.color}>{cfg.icon} {insp.overall||"—"}</Badge>
                    {(insp.findings||[]).length>0&&(
                      <span style={{fontSize:10,color:T.red}}>🔗{insp.findings.length}</span>
                    )}
                    {prevCount>0?(
                      <span style={{fontSize:10,color:T.muted}}>📅{prevCount} ant.</span>
                    ):null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── FINDING INLINE — create occurrence or service ────────────── */}
      {findingModal&&(
        <FindingCreationInline
          item={findingModal.inspFormItem}
          insp={findingModal.insp}
          employees={employees}
          onCreate={createFinding}
          onClose={()=>setFindingModal(null)}
        />
      )}

      {/* ── DETAIL INLINE ────────────────────────────────────────────── */}
      {detailInsp&&(
        <InspectionDetailInline
          insp={detailInsp}
          allInspections={visibleInspections}
          canComment={canManage || myLevelConfig?.canComment===true}
          showHistory={canManage || myLevelConfig?.canSeeHistory===true}
          myName={myName}
          displayRecordNumber={displayInspectionRecordNumber}
          onClose={()=>{ if(onBridgeBack) onBridgeBack(); else setDetailInsp(null); }}
          onAddComment={canManage||myLevel>=3 ? (findingIdx, comment) => {
            const updated = {...detailInsp,
              findings:(detailInsp.findings||[]).map((f,i)=>
                i===findingIdx?{...f,comments:[...(f.comments||[]),{text:comment,by:myName,ts:new Date().toISOString()}]}:f
              )
            };
            const nextUpdated = markOperationalUpdatePending({ ...updated, ...makeUpdateMeta(myName || "") }, { userName: myName || "", operationType:"inspection_comment" });
            persistInspections(p=>p.map(i=>i.id===nextUpdated.id?nextUpdated : i));
            setDetailInsp(nextUpdated);
            syncOnSave("inspections");
            show("💬 Comentário adicionado!");
          } : null}
        />
      )}
    </div>
  );
}

// ── Target card with location selector ───────────────────────────────────────
function InspectionTargetCard({ target, locations, costCenters, onStart }) {
  const SL = useSysLists();
  const [loc,  setLoc]  = useState(locations[0]?.id||"");
  const [cc,   setCc]   = useState(target.costCenter||COST_CENTERS[0]);
  const [open, setOpen] = useState(false);

  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",transition:"all .2s"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
      onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
      <div style={{background:`${T.accent}12`,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}
        onClick={()=>setOpen(!open)}>
        <span style={{fontSize:28}}>{target.icon}</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:T.text,fontSize:14}}>{target.label}</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{BASE_CHECKLIST[target.id]?.length||0} itens base</div>
        </div>
        <span style={{color:T.muted,fontSize:18,transition:"transform .2s",transform:open?"rotate(90deg)":"none"}}>›</span>
      </div>
      {open&&(
        <div style={{padding:"12px 16px",borderTop:`1px solid ${T.border}`}}>
          {locations.length>1&&(
            <Field label="Localização">
              <Select value={loc} onChange={e=>setLoc(e.target.value)}>
                {locations.map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Centro de Custo">
            <Select value={cc} onChange={e=>setCc(e.target.value)}>
              {(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Btn full style={{marginTop:8}} onClick={()=>{
            const locLabel=locations.find(l=>l.id===loc)?.label||loc;
            onStart(loc,locLabel,cc);
          }}>
            🔍 Iniciar Vistoria
          </Btn>
        </div>
      )}
    </div>
  );
}

// ── Finding creation inline ───────────────────────────────────────────────────
function FindingCreationInline({ item, insp, employees, onCreate, onClose }) {
  const [type,    setType]    = useState("occurrence");
  const [urgency, setUrgency] = useState(item.status==="Não Conforme"?"Alta":"Normal");
  const [assignee,setAssignee]= useState("");
  const [notes,   setNotes]   = useState(item.notes||"");

  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,marginTop:18,display:"flex",flexDirection:"column",gap:14,boxShadow:"0 18px 36px rgba(2,6,23,.08)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <div>
          <div style={{fontWeight:800,color:T.text,fontSize:17}}>⚡ Registrar Achado</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>Fluxo inline da vistoria — sem modal solto.</div>
        </div>
        <Btn variant="ghost" onClick={onClose}>Voltar</Btn>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {/* Item summary */}
        <div style={{background:`${FINDING_SEVERITY[item.status]?.color||T.muted}18`,border:`1px solid ${FINDING_SEVERITY[item.status]?.color||T.border}33`,borderRadius:10,padding:"10px 14px"}}>
          <div style={{fontWeight:700,color:T.text,fontSize:14}}>{item.desc}</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>
            {FINDING_SEVERITY[item.status]?.icon} {item.status} · {insp.locationLabel} · {insp.costCenter}
          </div>
        </div>

        {/* Type selector */}
        <div>
          <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>Criar como:</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[
              {t:"occurrence",icon:"🚨",label:"Ocorrência",desc:"Serviço de terceiros / reparo externo"},
              {t:"service",   icon:"🔨",label:"Serviço Interno",desc:"Tarefa para colaborador da fazenda"},
            ].map(opt=>(
              <div key={opt.t} onClick={()=>setType(opt.t)}
                style={{padding:"12px 14px",borderRadius:10,cursor:"pointer",
                  border:`2px solid ${type===opt.t?T.accent:T.border}`,
                  background:type===opt.t?`${T.accent}12`:T.card2,transition:"all .15s"}}>
                <div style={{fontSize:22,marginBottom:4}}>{opt.icon}</div>
                <div style={{fontWeight:700,color:type===opt.t?T.text:T.muted,fontSize:13}}>{opt.label}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{opt.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {type==="occurrence"&&(
          <Field label="Urgência">
            <Select value={urgency} onChange={e=>setUrgency(e.target.value)}>
              {["Normal","Alta","Urgente"].map(u=><option key={u}>{u}</option>)}
            </Select>
          </Field>
        )}

        {type==="service"&&(
          <Field label="Delegar para colaborador (opcional)">
            <Select value={assignee} onChange={e=>setAssignee(e.target.value)}>
              <option value="">Sem delegação</option>
              {employees.filter(e=>e.status==="Ativo"||e.active).map(e=>(
                <option key={e.id} value={e.name}>{e.name}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Observações adicionais">
          <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)}
            placeholder="Detalhes sobre o achado..."/>
        </Field>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={()=>onCreate(insp, item, type, {
            urgency, notes,
            ...(type==="service"&&assignee?{assignees:[{name:assignee,status:"Emitido"}]}:{}),
          })} style={{background:`linear-gradient(90deg,${T.red},#B91C1C)`,color:"#fff",border:"none"}}>
            ⚡ Criar {type==="occurrence"?"Ocorrência":"Serviço"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Inspection Detail inline ──────────────────────────────────────────────────
function InspectionDetailInline({ insp, allInspections=[], canComment=true, showHistory=true, myName="", displayRecordNumber=null, onAddComment, onClose }) {
  const [commentIdx, setCommentIdx] = useState(null);
  const [commentText, setCommentText] = useState("");
  const cfg    = FINDING_SEVERITY[insp.overall]||{color:T.muted,icon:"—"};
  const target = INSPECTION_TARGETS.find(t=>t.id===insp.targetId);

  // History: other inspections of same location, sorted newest first
  const locationHistory = allInspections
    .filter(i=>i.id!==insp.id && i.locationId===insp.locationId && i.targetId===insp.targetId && i.status==="Finalizada")
    .sort((a,b)=>new Date(b.finishedAt||b.startedAt)-new Date(a.finishedAt||a.startedAt))
    .slice(0,5);

  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,marginTop:18,display:"flex",flexDirection:"column",gap:0,boxShadow:"0 18px 36px rgba(2,6,23,.08)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:16}}>
        <div>
          <div style={{fontWeight:800,color:T.text,fontSize:17}}>Detalhes da Vistoria</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>Fluxo inline dentro do módulo Vistorias.</div>
        </div>
        <Btn variant="ghost" onClick={onClose}>Voltar à lista</Btn>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:0}}>
        {/* Header */}
        <div style={{background:`${cfg.color}18`,borderRadius:12,padding:"16px 18px",marginBottom:16,border:`1px solid ${cfg.color}33`}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <span style={{fontSize:32}}>{target?.icon||"📋"}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{typeof displayRecordNumber === "function" ? displayRecordNumber(insp) : (insp.recordNumber || insp.id)}</div>
              <div style={{fontWeight:800,color:T.text,fontSize:17,marginBottom:6}}>{insp.targetLabel}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Badge color={cfg.color}>{cfg.icon} {insp.overall}</Badge>
                <Badge color={T.muted}>📍 {insp.locationLabel}</Badge>
                <CCBadge cc={insp.costCenter}/>
              </div>
            </div>
          </div>
          <div style={{fontSize:12,color:T.muted,marginTop:10}}>
            👤 {insp.inspector} · {new Date(insp.finishedAt||insp.startedAt).toLocaleString("pt-BR")}
          </div>
        </div>

        {/* Summary counts */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
          {Object.entries(FINDING_SEVERITY).map(([s,c])=>{
            const cnt=insp.items.filter(i=>i.status===s).length;
            return(
              <div key={s} style={{background:T.card2,borderRadius:8,padding:"10px",textAlign:"center",border:`1px solid ${cnt>0?c.color:T.border}22`}}>
                <div style={{fontSize:20,marginBottom:2}}>{c.icon}</div>
                <div style={{fontSize:20,fontWeight:800,color:c.color}}>{cnt}</div>
                <div style={{fontSize:10,color:T.muted}}>{s}</div>
              </div>
            );
          })}
        </div>

        {/* Checklist items */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>📋 Itens Verificados</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {insp.items.map((item,i)=>{
              const ic=FINDING_SEVERITY[item.status]||{color:T.muted,icon:"—"};
              return(
                <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 12px",background:T.card2,borderRadius:8,borderLeft:`3px solid ${ic.color}`}}>
                  <span style={{fontSize:16,flexShrink:0,marginTop:1}}>{ic.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:T.text}}>{item.desc}</div>
                    {item.notes&&<div style={{fontSize:11,color:T.muted,marginTop:2}}>{item.notes}</div>}
                    {item.finding&&<div style={{fontSize:10,color:T.green,marginTop:3,fontWeight:600}}>🔗 {item.findingType==="occurrence"?"Ocorrência":"Serviço"} criado</div>}
                  </div>
                  {item.photo&&(
                    <img src={item.photo} alt="" style={{width:48,height:48,objectFit:"cover",borderRadius:6,flexShrink:0,cursor:"zoom-in"}}
                      onClick={()=>window.open(item.photo,"_blank")}/>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Findings */}
        {(insp.findings||[]).length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.red,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>⚡ Achados Registrados</div>
            {insp.findings.map((f,i)=>(
              <div key={i} style={{background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:8,padding:"10px 14px",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{f.itemDesc}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                  {f.type==="occurrence"?"🚨 Ocorrência":"🔨 Serviço"} criado automaticamente
                </div>
                {/* Comments */}
                {(f.comments||[]).length>0&&(
                  <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${T.border}33`}}>
                    {f.comments.map((c,ci)=>(
                      <div key={ci} style={{fontSize:11,color:T.muted,marginBottom:4}}>
                        <strong style={{color:T.text}}>{c.by}</strong>: {c.text}
                        <span style={{marginLeft:6,fontSize:9}}>{new Date(c.ts).toLocaleString("pt-BR")}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add comment — level 3+ */}
                {canComment&&onAddComment&&(
                  commentIdx===i?(
                    <div style={{marginTop:8,display:"flex",gap:6}}>
                      <Input value={commentText} placeholder="Seu comentário..." onChange={e=>setCommentText(e.target.value)} style={{fontSize:12,flex:1}}/>
                      <Btn size="sm" onClick={()=>{if(!commentText.trim())return;onAddComment(i,commentText);setCommentIdx(null);setCommentText("");}}>💬</Btn>
                      <Btn size="sm" variant="ghost" onClick={()=>{setCommentIdx(null);setCommentText("");}}>✕</Btn>
                    </div>
                  ):(
                    <button onClick={()=>setCommentIdx(i)}
                      style={{marginTop:6,background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:11,padding:0}}>
                      💬 Comentar
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        )}

        {/* General notes */}
        {insp.generalNotes&&(
          <div style={{background:T.card2,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>📝 Observações Gerais</div>
            <div style={{fontSize:13,color:T.text,lineHeight:1.7}}>{insp.generalNotes}</div>
          </div>
        )}

        {/* Histórico de vistorias anteriores neste local — level 2+ */}
        {showHistory!==false&&locationHistory.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>
              📅 Histórico deste Local ({locationHistory.length} vistoria(s) anterior(es))
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {locationHistory.map((h,i)=>{
                const hcfg=FINDING_SEVERITY[h.overall]||{color:T.muted,icon:"—"};
                return(
                  <div key={i} style={{display:"grid",gridTemplateColumns:"auto 1fr auto auto",gap:12,alignItems:"center",padding:"10px 12px",background:T.card2,borderRadius:8,border:`1px solid ${hcfg.color}22`}}>
                    <span style={{fontSize:18}}>{hcfg.icon}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:T.text}}>{new Date(h.finishedAt||h.startedAt).toLocaleDateString("pt-BR",{day:"2-digit",month:"short",year:"numeric"})}</div>
                      <div style={{fontSize:10,color:T.muted}}>👤 {h.inspector}</div>
                    </div>
                    <Badge color={hcfg.color}>{h.overall}</Badge>
                    <div style={{textAlign:"right",fontSize:10,color:T.muted}}>
                      {h.nonConformes>0&&<div style={{color:T.red}}>{h.nonConformes} NC</div>}
                      {h.atencoes>0&&<div style={{color:T.yellow}}>{h.atencoes} AT</div>}
                      {(h.findings||[]).length>0&&<div style={{color:T.accent}}>🔗{h.findings.length}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Assinatura digital */}
        {insp.signature&&(
          <div style={{background:`${T.green}10`,border:`1px solid ${T.green}33`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>✍️ Assinatura Digital</div>
            <div className="fz-app-header-left" style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:`${T.green}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                {insp.signature.role==="admin"?"👑":insp.signature.role==="gestor"?"🔐":"👤"}
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,color:T.text,fontSize:13}}>{insp.signature.name}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:1}}>
                  {insp.signature.role==="admin"?"Administrador":"Gestor"} · @{insp.signature.username}
                </div>
                <div style={{fontSize:10,color:T.green,marginTop:2}}>
                  ✅ Vistoria assinada em {new Date(insp.signature.signedAt).toLocaleString("pt-BR")}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"cursive",fontSize:18,color:T.green,opacity:.7}}>{insp.signature.name}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


  return InspectionsModule;
}
